import { ref, computed, inject } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth.store";
import ToolsService from "@/services/ToolsService";
import HomeRepository from "@/repositories/HomeRepository";
import { Contribution } from "@/models/Contribution";
import { Credit } from "@/models/Credit";
import { Saving } from "@/models/Saving"; // Asegúrate de tener este modelo definido
import { AxiosResponse } from "axios";
import Toast from "@/plugins/Toast";
import { ToastType } from "@/types/toast";
import { useCreditStore } from "@/store/credit.store";
import { useContributionStore } from "@/store/contribution.store";
import { useSavingStore } from "@/store/saving.store";

export default class HomeViewModel {
  public toast: Toast;
  public loading = ref<boolean>(false);
  public contributions = ref<Contribution[]>([]);
  public credits = ref<Credit[]>([]);
  public savers = ref<Saving[]>([]);
  public transactions = ref<Contribution[]>([]);

  private authStore = useAuthStore();
  private creditStore = useCreditStore();
  private contributionStore = useContributionStore();
  private savingStore = useSavingStore();
  private toolsService = new ToolsService();
  private router = useRouter();

  // Computed Properties
  public user = computed(() => this.authStore.getUserData);
  public userName = computed(() => this.user.value?.nom || "Usuario");
  public userEmail = computed(() => this.user.value?.email || "");
  public userInitial = computed(() =>
    this.toolsService.inicial(this.user.value?.nom || "U")
  );

  public assessorName = computed(
    () => this.user.value?.gestor_nom || "No asignado"
  );
  public assessorPhone = computed(
    () => this.user.value?.gestor_telmovil || this.user.value?.gestor_telfijo
  );
  public userHasAssessor = computed(() => !!this.user.value?.gestor_nom);

  constructor() {
    this.toast = inject("toast") as Toast;
    this.loadData();
  }

  /**
   * Load data from API and populate state.
   */
  async loadData(): Promise<void> {
    this.loading.value = true;
    console.log(this.authStore.getUserData);
    try {
      const authBasic = btoa(
        `${this.authStore.getLogin}:${this.authStore.getDecryptedPassword}`
      );
      const response: AxiosResponse = await HomeRepository.fetchUserData(
        authBasic
      );

      const { data } = response.data;
      console.log(data);
      this.contributions.value = data?.contributions || [];
      this.credits.value = data?.credits || [];
      this.savers.value = data?.savers || [];
      this.transactions.value = data?.last_transactions || [];

      this.loading.value = false;
    } catch (error) {
      console.error("Error fetching data:", error);
      this.toast.showToast(
        "Hubo un problema al cargar los datos. Por favor, intenta de nuevo.",
        ToastType.Error
      );
      this.loading.value = false;
    }
  }

  /**
   * Navigate to contributions page.
   */
  goToContributions(contribution: Contribution): void {
    this.contributionStore.setSelectedContribution(contribution);
    this.router.push({
      path: "/app/contribution-detail",
    });
  }

  /**
   * Navigate to credit detail page.
   */
  goToCreditDetail(credit: Credit): void {
    this.creditStore.setSelectedCredit(credit); // Guardar en el store
    this.router.push({
      path: "/app/credit-detail",
    });
  }

  /**
   * Navigate to credit detail page.
   */
  goToSaving(saving: Saving): void {
    this.savingStore.setSelectedSaving(saving); // Guardar en el store
    this.router.push({
      path: "/app/saving-detail",
    });
  }
  /**
   * Format currency based on coin type.
   */
  formatCoin(coin: number): string {
    return this.toolsService.getCoin(coin);
  }

  /**
   * Format float numbers with localization.
   */
  formatFloat(number: number): string {
    return this.toolsService.getFloat(number);
  }

  /**
   * Calculate percentage of disbursement.
   */
  percentDisbursement(total: number, remaining: number): number {
    return this.toolsService.percentDisbursement(total, remaining);
  }

  /**
   * Format transaction date.
   */
  formatDate(date: string): string {
    return this.toolsService.formatDate(date);
  }

  /**
   * Format transaction hour.
   */
  formatHour(hour: string): string {
    console.log(hour);
    return this.toolsService.formatHourISO(hour);
  }
  /**capitalize letter */
  capitalize(word: string): string {
    return this.toolsService.capitalize(word);
  }

  hideWords(word: string): string {
    return this.toolsService.hideWords(word);
  }
  hideEmailCharacters(word: string): string {
    return this.toolsService.hideEmailCharacters(word);
  }
  callAssessor(assessorPhone: string): void {
    console.log(assessorPhone);
  }
  getColor(tipoMov: string): string {
    switch (tipoMov) {
      case "1": // Movimiento de ingreso
        return "success"; // Verde
      case "2": // Movimiento de retiro
        return "error"; // Rojo
      default: // Otro tipo de movimiento
        return "primary"; // Color predeterminado
    }
  }

  getIconName(tipoMov: string): string {
    switch (tipoMov) {
      case "1": // Movimiento de ingreso
        return "mdi-arrow-top-right"; // Flecha hacia arriba
      case "2": // Movimiento de retiro
        return "mdi-arrow-bottom-left"; // Flecha hacia abajo
      default: // Otro tipo de movimiento
        return "mdi-wallet"; // Ícono genérico
    }
  }
}
